import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLogs, getUsersLookup } from '../../services/api';
import '../../styles/admin-ui.css';
import './Logs.css';

const LEVEL_CHIP = {
  info: 'signal',
  debug: '',
  warning: 'warn',
  error: 'danger',
};

const CATEGORY_NAMES = {
  auth: 'Authentication',
  system: 'System',
  server: 'Server',
  connection: 'Connection',
  vpn: 'VPN',
  license: 'License',
  security: 'Security',
  backup: 'Backup',
  agent: 'Agent',
  mailer: 'Email',
};

const Logs = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filterLevel, setFilterLevel] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useState('');

  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = {
        limit,
        offset: (currentPage - 1) * limit,
      };

      if (filterLevel !== 'all') {
        filters.level = filterLevel;
      }
      if (filterCategory !== 'all') {
        filters.category = filterCategory;
      }
      if (selectedUserId) {
        filters.user_id = selectedUserId;
      }

      const data = await getLogs(filters);
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.logs)
          ? data.logs
          : [];
      setLogs(list);
      setTotalCount(Number(data?.total ?? list.length) || 0);
      if (list.length === 0 && !data?.total) {
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setLogs([]);
      setTotalCount(0);
      setError(err.message || 'An error occurred while loading logs');
    } finally {
      setLoading(false);
    }
  }, [filterLevel, filterCategory, selectedUserId, currentPage, limit]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (userSearchQuery.length < 2) {
      setUserSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const users = await getUsersLookup(userSearchQuery);
        setUserSuggestions(Array.isArray(users) ? users : []);
      } catch (err) {
        console.error('Failed to fetch users:', err);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [userSearchQuery]);

  const handleUserSelect = (user) => {
    setSelectedUserId(user.id);
    setSelectedUserEmail(user.email);
    setUserSearchQuery('');
    setShowUserDropdown(false);
    setCurrentPage(1);
  };

  const handleClearUserFilter = () => {
    setSelectedUserId(null);
    setSelectedUserEmail('');
    setUserSearchQuery('');
  };

  const handleFilterLevelChange = (level) => {
    setFilterLevel(level);
    setCurrentPage(1);
  };

  const handleFilterCategoryChange = (e) => {
    setFilterCategory(e.target.value);
    setCurrentPage(1);
  };

  const getCategoryName = (category) => CATEGORY_NAMES[category] || category || '—';

  const getLevelChipClass = (level) => {
    const variant = LEVEL_CHIP[level];
    return variant ? `aui-chip ${variant}` : 'aui-chip';
  };

  const filteredLogs = logs.filter((log) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      log.message?.toLowerCase().includes(query) ||
      log.user_email?.toLowerCase().includes(query) ||
      log.category?.toLowerCase().includes(query)
    );
  });

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  const categories = ['all', ...new Set(logs.map((l) => l.category).filter(Boolean))];

  return (
    <div className="aui-page logs-page">
      <button type="button" className="aui-back" onClick={() => navigate('/dashboard/admin')}>
        ← Back to Admin
      </button>

      <div className="aui-hero">
        <div>
          <p className="aui-kicker">Observability</p>
          <h1>System logs</h1>
          <p className="aui-lead">Inspect activity, filter by level or user, and page through records.</p>
        </div>
        <div className="aui-hero-actions">
          <button type="button" className="aui-btn ghost" onClick={fetchLogs} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="aui-error">
          <span>{error}</span>
          <button type="button" className="aui-btn ghost" onClick={fetchLogs}>
            Retry
          </button>
        </div>
      )}

      <div className="aui-stats">
        <article className="aui-stat">
          <span>Total (API)</span>
          <strong>{totalCount}</strong>
        </article>
        <article className="aui-stat">
          <span>This page</span>
          <strong>{logs.length}</strong>
        </article>
        <article className="aui-stat">
          <span>Showing</span>
          <strong>{filteredLogs.length}</strong>
        </article>
        <article className="aui-stat">
          <span>Page</span>
          <strong>
            {currentPage}/{totalPages}
          </strong>
        </article>
      </div>

      <div className="aui-toolbar">
        <div className="aui-filters">
          {['all', 'info', 'warning', 'error', 'debug'].map((level) => (
            <button
              key={level}
              type="button"
              className={`aui-filter ${filterLevel === level ? 'is-active' : ''}`}
              onClick={() => handleFilterLevelChange(level)}
            >
              {level === 'all' ? 'All levels' : level}
            </button>
          ))}
        </div>
      </div>

      <div className="logs-controls">
        <input
          type="text"
          className="aui-search"
          placeholder="Search this page (message, user)…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <div className="logs-user-lookup">
          {selectedUserEmail ? (
            <div className="logs-selected-user">
              <span className="aui-chip signal">{selectedUserEmail}</span>
              <button type="button" className="aui-btn ghost" onClick={handleClearUserFilter}>
                Clear user
              </button>
            </div>
          ) : (
            <>
              <input
                type="text"
                className="aui-search"
                placeholder="Filter by user email…"
                value={userSearchQuery}
                onChange={(e) => {
                  setUserSearchQuery(e.target.value);
                  setShowUserDropdown(true);
                }}
                onFocus={() => setShowUserDropdown(true)}
              />
              {showUserDropdown && userSuggestions.length > 0 && (
                <div className="logs-user-dropdown">
                  {userSuggestions.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      className="logs-user-option"
                      onClick={() => handleUserSelect(user)}
                    >
                      <span>{user.email}</span>
                      <em>ID {user.id}</em>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <select
          className="logs-select"
          value={filterCategory}
          onChange={handleFilterCategoryChange}
        >
          <option value="all">All categories</option>
          {categories
            .filter((c) => c !== 'all')
            .map((cat) => (
              <option key={cat} value={cat}>
                {getCategoryName(cat)}
              </option>
            ))}
        </select>
      </div>

      {loading && logs.length === 0 ? (
        <div className="aui-empty">
          <div className="aui-spinner" />
          <p>Loading logs…</p>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="aui-empty">
          <h3>No logs</h3>
          <p>Nothing matched the current filters or search.</p>
        </div>
      ) : (
        <div className="aui-panel">
          <div className="logs-list">
            {filteredLogs.map((log) => (
              <article key={log.id} className="log-entry">
                <div className="log-entry-meta">
                  <span className={getLevelChipClass(log.level)}>{log.level || 'info'}</span>
                  <span className="aui-chip">{getCategoryName(log.category)}</span>
                  <time className="log-time">{log.created_at}</time>
                </div>
                <p className="log-message">{log.message}</p>
                <div className="log-source">
                  {log.user_email ? (
                    <span>{log.user_email}</span>
                  ) : (
                    <span className="log-system">System</span>
                  )}
                  {log.user_id != null && <span className="aui-chip">ID {log.user_id}</span>}
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {totalCount > limit && (
        <div className="logs-pagination">
          <button
            type="button"
            className="aui-btn ghost"
            disabled={currentPage === 1 || loading}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages} · {totalCount} total
          </span>
          <button
            type="button"
            className="aui-btn ghost"
            disabled={currentPage >= totalPages || loading}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Logs;
